import React, { useEffect, useState, useCallback } from 'react';
import { fetchExecution, startStep, prepStep, completeStep, adjustStepTime } from '../api/actions';
import StepBlock from './StepBlock';
import { formatDateTime, roundToNearestFiveMinutes } from '../utils/time';
import './RecipeExecutionView.css';

const WIDE_BREAKPOINT = '(min-width: 900px)';

// Compute dynamic time-until-end for a step, capping elapsed time
// so overruns don't underestimate remaining time
function computeStepTimeUntilEnd(step) {
    if (step.status === 'Completed') return 0;

    const now = Date.now();
    if (step.prepped_at) {
        const elapsed = (now - new Date(step.prepped_at).getTime()) / 1000;
        return step.time_until_end - step.preparation_time - Math.min(elapsed, step.waiting_time);
    }
    if (step.started_at) {
        const elapsed = (now - new Date(step.started_at).getTime()) / 1000;
        return step.time_until_end - Math.min(elapsed, step.preparation_time);
    }
    return step.time_until_end;
}

function computeSortTimer(step, globalRemainingTime) {
    if (step.status === 'StandBy') {
        if (globalRemainingTime > 0) {
            return globalRemainingTime - step.time_until_end;
        }
        return Infinity;
    }
    if (step.status === 'Started') {
        if (step.preparation_time > 0 && step.started_at) {
            const elapsed = (Date.now() - new Date(step.started_at).getTime()) / 1000;
            return Math.max(0, step.preparation_time - elapsed);
        }
        return Infinity;
    }
    if (step.status === 'Prepped') {
        if (step.waiting_time > 0 && step.prepped_at) {
            const elapsed = (Date.now() - new Date(step.prepped_at).getTime()) / 1000;
            return Math.max(0, step.waiting_time - elapsed);
        }
        return Infinity;
    }
    return Infinity;
}

export default function RecipeExecutionView({ executionId }) {
    const [execution, setExecution] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isWideScreen, setIsWideScreen] = useState(
        () => window.matchMedia(WIDE_BREAKPOINT).matches
    );

    useEffect(() => {
        const mq = window.matchMedia(WIDE_BREAKPOINT);
        const handler = (e) => setIsWideScreen(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    const loadExecution = useCallback(async () => {
        try {
            const data = await fetchExecution(executionId);
            setExecution(data);
            setError(null);
        } catch (err) {
            console.error('Failed to load execution:', err);
            setError('Failed to load execution');
        } finally {
            setLoading(false);
        }
    }, [executionId]);

    useEffect(() => {
        loadExecution();
    }, [loadExecution]);

    // Compute dynamic remaining time every second so StandBy "start in"
    // timers adjust when active steps overrun their expected durations
    const [dynamicRemainingTime, setDynamicRemainingTime] = useState(0);

    useEffect(() => {
        if (!execution) return;

        const compute = () => {
            let maxTime = 0;
            for (const step of Object.values(execution.step_executions)) {
                maxTime = Math.max(maxTime, computeStepTimeUntilEnd(step));
            }
            setDynamicRemainingTime(maxTime);
        };

        compute();
        const interval = setInterval(compute, 1000);
        return () => clearInterval(interval);
    }, [execution]);

    const handleStart = async (stepId) => {
        try {
            const updated = await startStep(executionId, stepId);
            setExecution(updated);
        } catch (err) {
            console.error('Failed to start step:', err);
        }
    };

    const handlePrep = async (stepId) => {
        try {
            const updated = await prepStep(executionId, stepId);
            setExecution(updated);
        } catch (err) {
            console.error('Failed to prep step:', err);
        }
    };

    const handleComplete = async (stepId) => {
        try {
            const updated = await completeStep(executionId, stepId);
            setExecution(updated);
        } catch (err) {
            console.error('Failed to complete step:', err);
        }
    };

    const handleAdjustTime = async (stepId, delta) => {
        try {
            const updated = await adjustStepTime(executionId, stepId, delta);
            setExecution(updated);
        } catch (err) {
            console.error('Failed to adjust step time:', err);
        }
    };

    if (loading) {
        return <div className="execution-view loading">Loading execution...</div>;
    }

    if (error || !execution) {
        return <div className="execution-view error">{error || 'Execution not found'}</div>;
    }

    const endTime = roundToNearestFiveMinutes(new Date(Date.now() + dynamicRemainingTime * 1000));

    const getStepProps = (step) => {
        switch (step.status) {
            case 'StandBy':
                return {
                    color: 'yellow',
                    onAction: () => handleStart(step.id),
                    actionLabel: 'Start',
                    showStartTimer: true,
                    globalRemainingTime: dynamicRemainingTime,
                };
            case 'Started':
                return {
                    color: 'green',
                    onAction: () => handlePrep(step.id),
                    actionLabel: 'Prepared',
                    showPrepTimer: true,
                };
            case 'Prepped':
                return {
                    color: 'blue',
                    showTimer: true,
                    onAction: () => handleComplete(step.id),
                    actionLabel: 'Complete',
                    onAdjustTime: (delta) => handleAdjustTime(step.id, delta),
                };
            default:
                return {};
        }
    };

    // Group steps by status for column layout
    const allSteps = Object.values(execution.step_executions);
    const activeSteps = allSteps.filter(s => s.status === 'StandBy' || s.status === 'Started' || s.status === 'Prepped');

    const sortByTimeUntilEnd = (a, b) => b.time_until_end - a.time_until_end;

    const standBySteps = activeSteps.filter(s => s.status === 'StandBy').sort(sortByTimeUntilEnd);
    const preparingSteps = activeSteps.filter(s => s.status === 'Started').sort(sortByTimeUntilEnd);
    const waitingSteps = activeSteps.filter(s => s.status === 'Prepped').sort(sortByTimeUntilEnd);

    // Flat sorted list for mobile
    const sortedSteps = [...activeSteps]
        .map(step => ({ step, sortTimer: computeSortTimer(step, dynamicRemainingTime) }))
        .sort((a, b) => a.sortTimer - b.sortTimer)
        .map(item => item.step);

    return (
        <div className={`execution-view${isWideScreen ? ' wide' : ''}`}>
            <div className="execution-header">
                <span className="execution-end-time">Ready by {formatDateTime(endTime)}</span>
            </div>

            {activeSteps.length === 0 ? (
                <div className="completion-card">
                    <div className="completion-checkmark">&#10003;</div>
                    <div className="completion-title">Bon app&eacute;tit!</div>
                    <div className="completion-subtitle">
                        All {allSteps.length} steps completed
                    </div>
                </div>
            ) : isWideScreen ? (
                <div className="steps-columns">
                    <div className="steps-column">
                        <div className="column-header column-header-yellow">Ready to Start ({standBySteps.length})</div>
                        {standBySteps.map(step => (
                            <StepBlock key={step.id} step={step} defaultExpanded {...getStepProps(step)} />
                        ))}
                    </div>
                    <div className="steps-column">
                        <div className="column-header column-header-green">Preparing ({preparingSteps.length})</div>
                        {preparingSteps.map(step => (
                            <StepBlock key={step.id} step={step} defaultExpanded {...getStepProps(step)} />
                        ))}
                    </div>
                    <div className="steps-column">
                        <div className="column-header column-header-blue">Waiting ({waitingSteps.length})</div>
                        {waitingSteps.map(step => (
                            <StepBlock key={step.id} step={step} defaultExpanded {...getStepProps(step)} />
                        ))}
                    </div>
                </div>
            ) : (
                <div className="steps-list">
                    {sortedSteps.map(step => (
                        <StepBlock key={step.id} step={step} {...getStepProps(step)} />
                    ))}
                </div>
            )}
        </div>
    );
}
